import { useRouter } from 'expo-router';
import React, { useMemo } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import type { FixtureWithRefs, TeamRef } from '@/lib/data/provider';
import { formatKickoffTime, formatMatchDate, formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { localizeTeamName } from '@/lib/i18n/footballNames';

// Knockout stage codes (round labels), ordered from earliest knockout to the final.
const KNOCKOUT_ORDER = [
  'LAST_32',
  'LAST_16',
  'QUARTER_FINALS',
  'SEMI_FINALS',
  'THIRD_PLACE',
  'FINAL',
] as const;
type KnockoutStage = (typeof KNOCKOUT_ORDER)[number];

const STAGE_KEY: Record<KnockoutStage, string> = {
  LAST_32: 'leagues.stageLast32',
  LAST_16: 'leagues.stageLast16',
  QUARTER_FINALS: 'leagues.stageQuarter',
  SEMI_FINALS: 'leagues.stageSemi',
  THIRD_PLACE: 'leagues.stageThird',
  FINAL: 'leagues.stageFinal',
};

const KNOCKOUT_SET = new Set<string>(KNOCKOUT_ORDER);

/**
 * Map a fixture's raw `round` to a knockout stage code, or null for a group /
 * league matchday. Fixtures store the PROVIDER's natural round string
 * ("Round of 32", "Quarter-finals", "Final", 365's "1/8" …) — the same strings
 * the bracket-scoring SQL classifies (migration 110) — so the display layer must
 * normalise them here rather than expecting the internal codes. Already-coded
 * values (LAST_32 …) pass straight through. Order matters: 3rd-place and semi are
 * tested before the bare "final" catch so "3rd Place Final" / "Semi-finals" land
 * in the right column.
 */
export function roundToStage(round: string | null): KnockoutStage | null {
  if (!round) return null;
  if (KNOCKOUT_SET.has(round)) return round as KnockoutStage;
  const r = round.toLowerCase();
  if (/round of 32|1\/16|last 32/.test(r)) return 'LAST_32';
  if (/round of 16|1\/8|eighth|last 16/.test(r)) return 'LAST_16';
  if (/quarter|1\/4/.test(r)) return 'QUARTER_FINALS';
  if (/3rd|third/.test(r)) return 'THIRD_PLACE';
  if (/semi|1\/2/.test(r)) return 'SEMI_FINALS';
  if (/final/.test(r)) return 'FINAL';
  return null;
}

/** Whether a fixture's round label is a knockout stage (vs a group/league matchday). */
export function isKnockoutRound(round: string | null): boolean {
  return roundToStage(round) !== null;
}

function TeamLine({ team, score, won }: { team: TeamRef | null; score: number | null; won: boolean }) {
  const { numeralOptions, locale } = useLocale();
  return (
    <View className="flex-row items-center gap-2">
      <TeamCrest uri={team?.logo_url} name={team ? localizeTeamName(team, locale) : 'TBD'} size="sm" />
      <AppText
        variant="caption"
        weight={won ? 'bold' : 'medium'}
        className={`flex-1 ${won ? 'text-ink' : 'text-soft'}`}
        numberOfLines={1}
      >
        {localizeTeamName(team, locale, true)}
      </AppText>
      {score != null ? (
        <AppText variant="caption" weight="bold" tabular className={won ? 'text-volt-bright' : 'text-muted'}>
          {formatNumber(score, numeralOptions)}
        </AppText>
      ) : null}
    </View>
  );
}

function MatchCard({ fixture }: { fixture: FixtureWithRefs }) {
  const router = useRouter();
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const finished = fixture.status === 'finished';
  const hs = fixture.home_score;
  const as = fixture.away_score;
  const decided = finished && hs != null && as != null;
  const kickoffDate = new Date(fixture.kickoff_at);
  const kickoff = Number.isNaN(kickoffDate.getTime()) ? null : kickoffDate;
  const footer = finished
    ? t('match.fullTime')
    : fixture.status === 'live'
      ? t('common.live')
      : kickoff
        ? `${formatMatchDate(kickoff, numeralOptions)} · ${formatKickoffTime(kickoff, numeralOptions)}`
        : '';

  return (
    <Pressable
      accessibilityRole="button"
      onPress={() => router.push(`/match/${fixture.id}`)}
      className="gap-2 rounded-xl border border-line bg-surface px-3 py-2.5 active:bg-surface-2/60"
      style={{ width: 184 }}
    >
      <TeamLine team={fixture.home_team} score={finished ? hs : null} won={decided && hs! > as!} />
      <TeamLine team={fixture.away_team} score={finished ? as : null} won={decided && as! > hs!} />
      <AppText variant="micro" className="text-muted" numberOfLines={1}>
        {footer}
      </AppText>
    </Pressable>
  );
}

/** Round-by-round knockout columns (horizontally scrollable), final on the right. */
export function KnockoutBracket({ fixtures }: { fixtures: FixtureWithRefs[] }) {
  const { t } = useTranslation();
  const columns = useMemo(
    () =>
      KNOCKOUT_ORDER.map((stage) => ({
        stage,
        matches: fixtures.filter((fixture) => roundToStage(fixture.round) === stage),
      })).filter((column) => column.matches.length > 0),
    [fixtures],
  );

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{ paddingHorizontal: 16, gap: 14 }}
    >
      {columns.map((column) => (
        <View key={column.stage} className="gap-3" style={{ width: 184 }}>
          <View className="self-start rounded-full bg-volt/15 px-3 py-1">
            <AppText variant="micro" weight="bold" className="uppercase tracking-wide text-volt">
              {t(STAGE_KEY[column.stage])}
            </AppText>
          </View>
          <View className="flex-1 justify-center gap-3">
            {column.matches.map((fixture) => (
              <MatchCard key={fixture.id} fixture={fixture} />
            ))}
          </View>
        </View>
      ))}
    </ScrollView>
  );
}
